import { ApiError } from "@/clients/core/ApiError";
import { env } from "@/constants/env";
import { QueryKeys } from "@/constants/query-keys";
import { QueryStaleTime } from "@/constants/query-stale-time";
import { ignoredErrors, normalizeErrorMessage } from "@/utils/error-message";
import { reportSubscriptionStatusError } from "@/utils/subscription/subscription-status-monitoring";
import { persistQueryClient } from "@tanstack/query-persist-client-core";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import {
  MutationCache,
  MutationKey,
  QueryCache,
  QueryClient,
  QueryKey,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { format } from "date-fns";

const logQuery = ({
  key,
  style,
  data,
  error,
  updatedAt,
}: {
  key: QueryKey | undefined;
  style: string;
  data?: unknown;
  error?: unknown;
  updatedAt?: number;
}) => {
  if (env.MODE === "development") {
    console.group(`%c Query [${key}]`, style);
    if (updatedAt) {
      console.log("Query Updated At:", format(new Date(updatedAt), "yyyy-MM-dd HH:mm:ss"));
    }
    if (data) {
      console.log("Response Data:", data);
    }

    if (error) {
      const normalizedMessage = normalizeErrorMessage(error);
      if (normalizedMessage && error instanceof Error && normalizedMessage !== error.message) {
        error.message = normalizedMessage;
      }
      console.info(typeof error);
      if (error instanceof ApiError) {
        console.info(error.body.detail);
        if (error.status === 401) return;
      }
      console.error("Error:", error);
    }
    console.groupEnd();
  } else {
    if (error) {
      const normalizedMessage = normalizeErrorMessage(error);
      if (normalizedMessage && error instanceof Error && normalizedMessage !== error.message) {
        error.message = normalizedMessage;
      }
      console.info(typeof error);
      console.info("error", error);
      if (error instanceof ApiError) {
        if (error.status === 404) {
          return;
        }
        if (error.status === 401) {
          return;
        }
        if (error.status === 404) {
          return;
        }
        console.info(error.body.detail);
      } else if (error instanceof AxiosError) {
        console.info(error.message);
      } else {
        console.error(error);
      }
    }
    if (data) {
      console.log(`${key} Response Data: `);
      console.log(JSON.stringify(data));
    }
  }
};

const logMutation = ({
  key,
  message,
  variables,
  error,
  data,
  style,
}: {
  key: MutationKey | undefined;
  message: string;
  variables: unknown;
  context: unknown;
  data?: unknown;
  error?: unknown;
  style?: string;
}) => {
  if (env.MODE === "development") {
    console.group(`%c Mutation [${key}]: ${message}`, style);
    console.log("Variables:", variables);
    if (data) {
      console.log("Response Data:", data);
    }

    if (error) {
      const normalizedMessage = normalizeErrorMessage(error);
      if (normalizedMessage && error instanceof Error && normalizedMessage !== error.message) {
        error.message = normalizedMessage;
      }
      if (error instanceof Error && ignoredErrors.has(error.message)) {
        console.warn("Ignored error:", error.message);
        console.groupEnd();
        return;
      }
      if (error instanceof ApiError) {
        console.info(error.body.detail);
      }
      console.error("Error:", error);
    }
    console.groupEnd();

    if (error instanceof ApiError) {
      if (error.status === 401) {
        return;
      }
    } else if (error instanceof AxiosError) {
      console.info(error.message);
    }
  } else {
    console.log(`${key} request: `, JSON.stringify(variables));

    if (error) {
      const normalizedMessage = normalizeErrorMessage(error);
      if (normalizedMessage && error instanceof Error && normalizedMessage !== error.message) {
        error.message = normalizedMessage;
      }
      console.info(typeof error);
      console.info("error", error);
      if (error instanceof Error && ignoredErrors.has(error.message)) {
        console.warn("Ignored error:", error.message);
        return;
      }
      if (error instanceof ApiError) {
        if (error.status === 401) {
          return;
        }
        if (error.status === 422) {
          console.info(error);
          return;
        }
        console.info(error.body.detail);
      } else if (error instanceof AxiosError) {
        console.info(error.message);
      } else {
        console.error(error);
      }
    }
    if (data) {
      console.log(JSON.stringify(data));
    }
  }
};

export const getQueryClient = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: QueryStaleTime.MEDIUM,
        retryOnMount: true,
      },
    },
    queryCache: new QueryCache({
      onSuccess: (data, query) => {
        logQuery({
          key: query.queryKey,
          style: "color: #00a650; font-weight: bold;",
          data: data,
          updatedAt: query.state.dataUpdatedAt,
        });
      },
      onError: (error, query) => {
        logQuery({
          key: query.queryKey,
          style: "color: #dc3545; font-weight: bold;",
          error: error,
        });
        // Structured capture for subscription-status failures (fires once per
        // failed query, after retries are exhausted).
        if (query.queryKey?.[0] === QueryKeys.SUBSCRIPTION_STATUS) {
          reportSubscriptionStatusError(error);
        }
      },
    }),
    mutationCache: new MutationCache({
      onSuccess: (data, variables, context, mutation) => {
        logMutation({
          key: mutation.options.mutationKey,
          style: "color: #00a650; font-weight: bold;",
          message: "Mutation Success",
          variables: variables,
          context: context,
          data: data,
        });
      },
      onError: (error, variables, context, mutation) => {
        logMutation({
          key: mutation.options.mutationKey,
          style: "color: #dc3545; font-weight: bold;",
          message: error.message,
          variables: variables,
          context: context,
          error: error,
        });
      },
    }),
  });

  const localStoragePersister = createSyncStoragePersister({
    storage: window.localStorage,
  });

  persistQueryClient({
    queryClient,
    persister: localStoragePersister,
    maxAge: 1000 * 60 * 60 * 24,
  });

  return queryClient;
};
